import React from 'react';
import { ChevronDown } from 'lucide-react';

interface StepLayoutProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  summary?: React.ReactNode;
  children?: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  hasContent?: boolean;
  isStreaming?: boolean;
  nested?: boolean;
}

export function StepLayout({
  icon,
  title,
  summary,
  children,
  isExpanded,
  onToggle,
  hasContent,
  isStreaming,
  nested = false,
}: StepLayoutProps) {
  const gutter = nested ? 'pl-1.5 -ml-1.5' : 'pl-2 -ml-2';
  return (
    <div
      className={`group border-l-2 border-transparent ${gutter} py-1 transition-all duration-200 hover:border-gray-100`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-start gap-3 transition-colors text-left ${!hasContent ? 'cursor-default' : 'cursor-pointer'}`}
        disabled={!hasContent}
      >
        <div
          className={`flex-shrink-0 flex items-center justify-center w-4 h-4 mt-0.5 ${isStreaming ? 'opacity-100' : 'opacity-70'}`}
        >
          {icon}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-2">
          <span className="text-xs font-medium tracking-tight text-gray-700 break-words shrink-0 sm:max-w-[40%]">
            {title}
          </span>
          {summary ? (
            <div className="flex-1 min-w-0 flex items-start gap-2 text-left">
              <span className="text-[10px] text-gray-300 flex-shrink-0 mt-0.5 hidden sm:inline" aria-hidden>
                •
              </span>
              <span className="text-[11px] text-gray-500 break-words whitespace-normal leading-snug line-clamp-3 sm:line-clamp-2">
                {summary}
              </span>
            </div>
          ) : null}
        </div>

        {hasContent && (
          <div
            className={`flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-gray-400' : 'text-gray-200 group-hover:text-gray-300'}`}
          >
            <ChevronDown size={10} strokeWidth={2} />
          </div>
        )}
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          <div className="pl-7 pb-1 pt-1 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="text-xs text-gray-600 border-l border-gray-100 pl-3 py-1">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
